import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getLogtoAccessToken } from '@/lib/logto';
import type { ArticleDetail, ArticleSummary } from '@/types/newsService';

interface ListArticlesParams {
  locale?: string;
  limit?: number;
  contentType?: string;
  tag?: string;
  sort?: 'latest' | 'trending' | 'top';
}

interface ListArticlesResponse {
  docs: ArticleSummary[];
  totalDocs: number;
  totalPages: number;
}

interface QNewsEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * QNews API client. Talks to qnews.qlash.gg (mounted at /qnews).
 * Auth piggybacks on the Logto access token (qnews-api validates with the
 * same JWKS as the Laravel monolith).
 */
export const newsApi = createApi({
  reducerPath: 'newsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: env.newsApiUrl,
    prepareHeaders: async (headers) => {
      const token = await getLogtoAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Articles', 'Tags'],
  endpoints: (builder) => ({
    listArticles: builder.query<ListArticlesResponse, ListArticlesParams | void>({
      query: (params) => ({
        url: '/qnews',
        params: params ?? undefined,
      }),
      transformResponse: (raw: QNewsEnvelope<ListArticlesResponse>) => raw.data,
      providesTags: ['Articles'],
    }),
    trendingArticles: builder.query<ListArticlesResponse, { limit?: number } | void>({
      query: (params) => ({
        url: '/qnews/trending',
        params: params ?? undefined,
      }),
      transformResponse: (raw: QNewsEnvelope<ListArticlesResponse>) => raw.data,
      providesTags: ['Articles'],
    }),
    getArticle: builder.query<ArticleDetail, string>({
      query: (slug) => `/qnews/${slug}`,
      transformResponse: (raw: QNewsEnvelope<ArticleDetail>) => raw.data,
      providesTags: (_r, _e, slug) => [{ type: 'Articles', id: slug }],
    }),
  }),
});

export const {
  useListArticlesQuery,
  useTrendingArticlesQuery,
  useGetArticleQuery,
} = newsApi;
